/**
 * Provisioning a device.
 *
 * Sites are created under a client, in the Clients section, so this only chooses
 * one. The dropdown shows "Client — Site" rather than a bare site name, because
 * "Head Office" on its own tells an operator with several clients nothing.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { fetchSites, provisionDevice, ApiError, type ApiSite } from './api.ts';
import { validateDevice } from './forms.ts';

interface Props {
  onProvisioned: (result: { claimCode: string; claimCodeExpiresAt: string; deviceName: string }) => void;
  onChanged: () => void;
  /** Bumped by the parent when clients or sites change, so the list stays current. */
  reloadKey: number;
}

export function Provision({ onProvisioned, onChanged, reloadKey }: Props): React.JSX.Element {
  const [sites, setSites] = useState<ApiSite[]>([]);
  const [siteId, setSiteId] = useState('');
  const [name, setName] = useState('');
  const [maxCameras, setMaxCameras] = useState('8');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await fetchSites();
      setSites(loaded);
      // Preselect when there is only one, which is the common case early on.
      if (loaded.length === 1) setSiteId(loaded[0]?.id ?? '');
    } catch {
      setMessage('Could not load sites.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setMessage(null);

    const check = validateDevice({ siteId, name, maxCameras });
    if (!check.ok) {
      setErrors(check.errors);
      return;
    }
    setErrors({});
    setBusy(true);

    try {
      const result = await provisionDevice({
        siteId,
        name: name.trim(),
        maxCameras: Number.parseInt(maxCameras.trim(), 10),
      });

      // Handed straight up. This response is the only place the code exists.
      onProvisioned({
        claimCode: result.claimCode,
        claimCodeExpiresAt: result.claimCodeExpiresAt,
        deviceName: result.device.name,
      });
      setName('');
      onChanged();
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'Could not provision the device.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
          {sites.length === 0 ? (
            <div className="empty">
              <p className="empty__title">No sites yet</p>
              <p>Add a client above, then create a site under it.</p>
            </div>
          ) : (
            <form className="form" onSubmit={(e) => void submit(e)}>
              <div className={errors['siteId'] ? 'field field--invalid' : 'field'}>
                <label htmlFor="device-site">Site</label>
                <select id="device-site" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                  <option value="">Choose a site…</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.client_name ? `${site.client_name} — ${site.name}` : site.name}
                    </option>
                  ))}
                </select>
                {errors['siteId'] ? <span className="field__error">{errors['siteId']}</span> : null}
              </div>

              <div className={errors['name'] ? 'field field--invalid' : 'field'}>
                <label htmlFor="device-name">Device name</label>
                <input
                  id="device-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Loading Dock NVR"
                />
                {errors['name'] ? <span className="field__error">{errors['name']}</span> : null}
              </div>

              <div className={errors['maxCameras'] ? 'field field--invalid' : 'field'}>
                <label htmlFor="device-max">Licensed cameras</label>
                <input
                  id="device-max"
                  inputMode="numeric"
                  value={maxCameras}
                  onChange={(e) => setMaxCameras(e.target.value)}
                />
                <span className="field__hint">
                  How many cameras this appliance is licensed for. Existing cameras keep recording if
                  the entitlement lapses, but no more can be added.
                </span>
                {errors['maxCameras'] ? <span className="field__error">{errors['maxCameras']}</span> : null}
              </div>

              <div className="form__actions">
                <button type="submit" className="btn btn--primary" disabled={busy}>
                  {busy ? 'Provisioning…' : 'Provision device'}
                </button>
              </div>
            </form>
          )}

      {message ? (
        <p className="notice notice--error" role="alert" style={{ marginTop: 'var(--s3)' }}>
          {message}
        </p>
      ) : null}
    </>
  );
}
